import { Trans } from '@lingui/react/macro'
import { t } from '@lingui/core/macro'
import { useState } from 'react'
import { useMutation } from '@connectrpc/connect-query'

import { StartPasswordResetRequestSchema } from '@jotta/grpc-connect-openapi/login'
import { startPasswordReset } from '@jotta/grpc-connect-openapi/loginQuery'
import { useForm } from '@jotta/hooks'
import { Form } from '@jotta/ui/Form'
import { FormField } from '@jotta/ui/FormField'
import { useBrandCode } from '@jotta/ui/useBrandTheme'

import { CTAButton } from './CTAButton'
import { generateSignupToken } from './signupForm'

function PasswordWasReset({ onClose }: { onClose: () => void }) {
  return (
    <>
      <p>
        <Trans>
          We have sent you an email with instructions for resetting your
          password. Please check your email and follow the instructions.
        </Trans>
      </p>
      <CTAButton type="submit" onClick={onClose}>
        <Trans>Continue</Trans>
      </CTAButton>
    </>
  )
}

function ResetPasswordForm({ onWasReset }: { onWasReset: () => void }) {
  const brandCode = useBrandCode()

  const { mutateAsync } = useMutation(startPasswordReset, {
    onSuccess: onWasReset,
    throwOnError: false,
  })

  const resetPasswordForm = useForm({
    fields: {
      email: {
        type: 'text',
        required: true,
        validate: email => {
          if (email && !/^\S+@\S+$/.test(email)) {
            return `We were unable to recognize "${email}" as an email address. Please look for typos and try again.`
          }
        },
      },
    },
    submit: async ({ email }) =>
      mutateAsync(
        await generateSignupToken(
          StartPasswordResetRequestSchema,
          {
            email,
            brandCode,
          },
          (message, token) => (message.token = token),
        ),
      ),
  })

  const { isSubmitting, fields } = resetPasswordForm
  const email = fields.email

  return (
    <Form form={resetPasswordForm} className="flex flex-col gap-4">
      <div className="pb-4">
        <Trans>
          Just enter the email you signed up with and we'll let you reset it.
        </Trans>
      </div>

      <FormField field={email} label={t`Email`}>
        <input className="input" {...email.inputProps} />
      </FormField>

      <CTAButton loading={isSubmitting} type="submit">
        <Trans>Reset password</Trans>
      </CTAButton>
    </Form>
  )
}

export function ResetPassword({ onClose }: { onClose: () => void }) {
  const [wasReset, setWasReset] = useState(false)
  return (
    <div className="flex flex-col gap-4">
      <h1 className="h1">
        <Trans>Forgot password?</Trans>
      </h1>
      {wasReset ? (
        <PasswordWasReset onClose={onClose} />
      ) : (
        <ResetPasswordForm onWasReset={() => setWasReset(true)} />
      )}
    </div>
  )
}
